import React, { useState, useEffect } from 'react';

interface StatsData {
  timestamp: string;
  requests: {
    total: number;
    byPath: Record<string, number>;
    byMethod: Record<string, number>;
    lastRequests: Array<{path: string, method: string, timestamp: number}>;
  };
  analytics: {
    pageViews: Record<string, number>;
    apiCalls: Record<string, number>;
    modelUploads: number;
    modelDownloads: number;
    lastEvents: Array<{type: string, path: string, timestamp: number}>;
    totalPageViews: number;
    totalApiCalls: number;
  };
  performance: {
    r2Operations: number;
    d1Operations: number;
    // другие поля производительности
  };
  db: {
    modelsCount: number;
  } | null;
  r2: {
    status: string;
  } | null;
}

const AdminPanel: React.FC = () => {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshInterval, setRefreshInterval] = useState<number>(5); // секунды
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Функция для загрузки статистики
  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/stats');
      
      if (!response.ok) {
        throw new Error(`Ошибка при получении статистики: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      setStats(data);
      setError(null);
    } catch (err) {
      console.error('Ошибка при получении статистики:', err);
      setError(err instanceof Error ? err.message : 'Неизвестная ошибка');
    } finally {
      setLoading(false);
    }
  };

  // Автоматическое обновление
  useEffect(() => {
    // Первоначальная загрузка
    fetchStats();
    
    // Настройка интервала обновления
    let intervalId: number | null = null;
    
    if (autoRefresh) {
      intervalId = window.setInterval(() => {
        fetchStats();
      }, refreshInterval * 1000);
    }
    
    // Очистка при размонтировании
    return () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
      }
    };
  }, [refreshInterval, autoRefresh]);

  // Форматирование даты
  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Панель администратора</h1>
      
      {/* Управление обновлением */}
      <div className="bg-card p-4 rounded-lg mb-6 flex items-center gap-4">
        <button 
          onClick={fetchStats}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md"
          disabled={loading}
        >
          {loading ? 'Загрузка...' : 'Обновить данные'}
        </button>
        
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="autoRefresh"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
            className="h-4 w-4"
          />
          <label htmlFor="autoRefresh">Автообновление</label>
        </div>
        
        {autoRefresh && (
          <div className="flex items-center gap-2">
            <label htmlFor="refreshInterval">Интервал (сек):</label>
            <input
              type="number"
              id="refreshInterval"
              min="1"
              max="60"
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(Number(e.target.value))}
              className="w-16 px-2 py-1 border border-border rounded"
            />
          </div>
        )}
        
        {stats && (
          <div className="text-sm text-muted-foreground ml-auto">
            Последнее обновление: {new Date(stats.timestamp).toLocaleString()}
          </div>
        )}
      </div>
      
      {/* Сообщение об ошибке */}
      {error && (
        <div className="bg-destructive/20 border border-destructive text-destructive p-4 rounded-lg mb-6">
          {error}
        </div>
      )}
      
      {/* Данные статистики */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Общая статистика */}
          <div className="bg-card p-4 rounded-lg">
            <h2 className="text-xl font-bold mb-4">Общая статистика</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-background p-3 rounded-md">
                <p className="text-muted-foreground text-sm">Всего запросов</p>
                <p className="text-2xl font-bold">{stats.requests.total}</p>
              </div>
              <div className="bg-background p-3 rounded-md">
                <p className="text-muted-foreground text-sm">API вызовов</p>
                <p className="text-2xl font-bold">{stats.analytics.totalApiCalls}</p>
              </div>
              <div className="bg-background p-3 rounded-md">
                <p className="text-muted-foreground text-sm">Загрузок моделей</p>
                <p className="text-2xl font-bold">{stats.analytics.modelUploads}</p>
              </div>
              <div className="bg-background p-3 rounded-md">
                <p className="text-muted-foreground text-sm">Скачиваний моделей</p>
                <p className="text-2xl font-bold">{stats.analytics.modelDownloads}</p>
              </div>
            </div>
            
            <h3 className="text-lg font-semibold mt-6 mb-3">Статус сервисов</h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center p-2 bg-background rounded">
                <span>База данных</span>
                <span className={`px-2 py-1 rounded text-xs ${stats.db ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {stats.db ? 'Доступна' : 'Недоступна'}
                </span>
              </div>
              <div className="flex justify-between items-center p-2 bg-background rounded">
                <span>Хранилище R2</span>
                <span className={`px-2 py-1 rounded text-xs ${stats.r2 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {stats.r2 ? 'Доступно' : 'Недоступно'}
                </span>
              </div>
            </div>
          </div>
          
          {/* API вызовы */}
          <div className="bg-card p-4 rounded-lg">
            <h2 className="text-xl font-bold mb-4">API вызовы</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-2">Эндпоинт</th>
                    <th className="text-right p-2">Вызовы</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(stats.analytics.apiCalls).map(([endpoint, count]) => (
                    <tr key={endpoint} className="border-b border-border">
                      <td className="p-2">{endpoint}</td>
                      <td className="text-right p-2">{count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Последние запросы */}
          <div className="bg-card p-4 rounded-lg md:col-span-2">
            <h2 className="text-xl font-bold mb-4">Последняя активность</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-2">Время</th>
                    <th className="text-left p-2">Тип</th>
                    <th className="text-left p-2">Путь</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.analytics.lastEvents.map((event, index) => (
                    <tr key={index} className="border-b border-border">
                      <td className="p-2">{formatDate(event.timestamp)}</td>
                      <td className="p-2">
                        <span className={`px-2 py-1 rounded text-xs 
                          ${event.type === 'apiCall' ? 'bg-blue-100 text-blue-800' : ''}
                          ${event.type === 'pageView' ? 'bg-gray-100 text-gray-800' : ''}
                          ${event.type === 'modelUpload' ? 'bg-green-100 text-green-800' : ''}
                          ${event.type === 'modelDownload' ? 'bg-orange-100 text-orange-800' : ''}
                        `}>
                          {event.type}
                        </span>
                      </td>
                      <td className="p-2">{event.path}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel; 